/**
 * AddMeetingManuallyView
 *
 * Allows providers to create meetings for pre-existing appointments
 * scheduled outside RepConnect (reverse of BookMeetingView).
 *
 * Flow:
 * 1. Location + Date/Time - From provider's own availability
 * 2. Sales Rep - Search RepConnect OR manual entry
 * 3. Restaurant (Optional)
 * 4. Notes & Confirm
 */

import React, { useState, useEffect } from 'react';
import {
  Calendar, User, Utensils,
  CheckCircle, ArrowLeft, ArrowRight, AlertCircle,
  Building
} from 'lucide-react';
import { meetingService } from '../utils/supabaseData';
import { supabase } from '../utils/supabase';
import SmartCalendar from '../components/SmartCalendar';
import LocationSelector from '../components/LocationSelector';
import SalesRepSelector from '../components/SalesRepSelector';
import YelpRestaurantSearch from '../components/YelpRestaurantSearch';
import emailService from '../utils/emailService';
import { logger } from '../utils/logger';

const AddMeetingManuallyView = ({
  currentUser,
  setCurrentView,
  preSelectedDate = null  // Optional: pre-fill from calendar click
}) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [savedMeeting, setSavedMeeting] = useState(null);
  const [error, setError] = useState(null);

  const [meetingData, setMeetingData] = useState({
    location: null,
    date: preSelectedDate || '',
    time: '',
    salesRep: null,        // { id, name, company, email, isExternal }
    restaurant: null,
    notes: ''
  });

  // Restaurant search modal
  const [showRestaurantSearch, setShowRestaurantSearch] = useState(false);

  // Pre-fill date if provided
  useEffect(() => {
    if (preSelectedDate) {
      setMeetingData(prev => ({ ...prev, date: preSelectedDate }));
    }
  }, [preSelectedDate]);

  const steps = [
    { number: 1, title: 'Date & Time', icon: Calendar },
    { number: 2, title: 'Sales Rep', icon: User },
    { number: 3, title: 'Restaurant', icon: Utensils },
    { number: 4, title: 'Confirm', icon: CheckCircle }
  ];

  const canProceed = () => {
    switch (step) {
      case 1:
        return meetingData.location && meetingData.date && meetingData.time;
      case 2:
        return meetingData.salesRep && meetingData.salesRep.email;
      case 3:
        return true; // Restaurant is optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  // Helper function to convert 12-hour to 24-hour format
  const convertTo24Hour = (time12h) => {
    if (!time12h) return '12:00:00';
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');

    if (hours === '12') {
      hours = '00';
    }
    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
  };

  // Helper function to calculate end time
  const calculateEndTime = (startTime, durationMinutes) => {
    const time24 = convertTo24Hour(startTime);
    const [hours, minutes] = time24.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    startDate.setMinutes(startDate.getMinutes() + durationMinutes);
    return startDate.toTimeString().slice(0, 8);
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const salesRep = meetingData.salesRep;

      // Build meeting data for Supabase
      const supabaseMeetingData = {
        // For manual meetings by provider, they are the customer
        customer_id: currentUser.id,

        // Sales rep - could be registered (has id) or external (id is null)
        sales_rep_id: salesRep.isExternal ? null : salesRep.id,

        // For external reps, store their info directly on the meeting
        external_sales_rep_name: salesRep.isExternal ? salesRep.name : null,
        external_sales_rep_company: salesRep.isExternal ? salesRep.company : null,
        external_sales_rep_email: salesRep.isExternal ? salesRep.email : null,
        external_sales_rep_phone: salesRep.isExternal ? (salesRep.phone || null) : null,

        // Meeting details
        meeting_date: meetingData.date,
        start_time: convertTo24Hour(meetingData.time),
        end_time: calculateEndTime(meetingData.time, 60),
        status: 'confirmed',  // Auto-confirmed since provider is creating it

        // Location
        location_id: meetingData.location?.location_id || meetingData.location?.id,
        location_name: meetingData.location?.practice_locations?.location_name || meetingData.location?.location_name,
        location_address: meetingData.location?.practice_locations?.address || meetingData.location?.address,

        // Restaurant (optional)
        restaurant_name: meetingData.restaurant?.name || null,
        restaurant_address: meetingData.restaurant?.address || null,
        restaurant_phone: meetingData.restaurant?.phone || null,
        restaurant_cuisine_type: meetingData.restaurant?.cuisineType || meetingData.restaurant?.cuisine || null,
        restaurant_price_range: meetingData.restaurant?.priceRange || meetingData.restaurant?.price || null,
        restaurant_rating: meetingData.restaurant?.rating || null,
        restaurant_url: meetingData.restaurant?.url || null,
        restaurant_yelp_id: meetingData.restaurant?.id || null,

        // Other fields
        booking_notes: meetingData.notes,
        meeting_type: 'lunch',
        is_manually_added: true,  // Flag to indicate this was added manually
        attendee_count: 2  // Provider + sales rep minimum
      };

      // Create meeting
      const newMeeting = await meetingService.createMeeting(supabaseMeetingData);

      if (!newMeeting) {
        throw new Error('Failed to create meeting');
      }

      // Create attendee records for BOTH the provider AND sales rep (required for calendar view)
      // The calendar uses get_meetings_for_attendee RPC which queries meeting_attendees
      const attendeeRecords = [
        {
          meeting_id: newMeeting.id,
          user_id: currentUser.id,
          attendee_type: 'physician',
          is_organizer: true,
          rsvp_status: 'accepted'  // Auto-accepted since they created it
        }
      ];

      // Add sales rep attendee record if they are a registered user (not external)
      if (!salesRep.isExternal && salesRep.id) {
        attendeeRecords.push({
          meeting_id: newMeeting.id,
          user_id: salesRep.id,
          attendee_type: 'sales_rep',
          is_organizer: false,
          rsvp_status: 'accepted'  // Auto-accepted for manually added meetings
        });
      }

      const { error: attendeeError } = await supabase
        .from('meeting_attendees')
        .insert(attendeeRecords);

      if (attendeeError) {
        logger.error('Error creating attendee records', { error: attendeeError.message });
        // Don't fail the whole operation - meeting was created successfully
      }

      // Send email notification to sales rep
      await sendSalesRepNotification(newMeeting);

      setSavedMeeting(newMeeting);
      setSubmitSuccess(true);

    } catch (err) {
      logger.error('Error creating manual meeting', { error: err.message });
      setError(err.message || 'Failed to create meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendSalesRepNotification = async (meeting) => {
    const salesRep = meetingData.salesRep;
    const providerName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A healthcare provider';
    const locationName = meetingData.location?.practice_locations?.location_name || meetingData.location?.location_name || 'Location TBD';

    try {
      if (salesRep.isExternal) {
        // External rep - send invitation to join RepConnect
        await emailService.sendEmail({
          to: salesRep.email,
          subject: `${providerName} has scheduled a meeting with you`,
          html: generateExternalRepEmailHtml({
            repName: salesRep.name,
            providerName,
            meetingDate: formatDateForDisplay(meetingData.date),
            meetingTime: meetingData.time,
            locationName,
            restaurantName: meetingData.restaurant?.name
          }),
          text: `Hi ${salesRep.name}, ${providerName} has scheduled a meeting with you on ${formatDateForDisplay(meetingData.date)} at ${meetingData.time}. Join RepConnect to manage all your meetings: https://login.meetwithreps.com`,
          emailType: 'meeting_notification'
        });
      } else {
        // Registered rep - send standard meeting notification
        await emailService.sendMeetingBookingNotification({
          physicianName: salesRep.name,  // Actually sending to sales rep
          physicianEmail: salesRep.email,
          salesRepName: providerName,  // Provider added it
          meetingDate: formatDateForDisplay(meetingData.date),
          meetingTime: meetingData.time,
          locationName,
          restaurantName: meetingData.restaurant?.name || 'Restaurant not specified',
          notes: `This meeting was added by ${providerName}. ${meetingData.notes || ''}`
        });
      }
      logger.debug('Sales rep notification sent successfully');
    } catch (err) {
      logger.error('Error sending sales rep notification', { error: err.message });
      // Don't throw - meeting was created successfully, just email failed
    }
  };

  const generateExternalRepEmailHtml = (details) => {
    return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #2563eb, #1d4ed8); color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0;">
      <h1 style="margin: 0; font-size: 24px;">Meeting Scheduled</h1>
    </div>
    <div style="background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; border-top: none;">
      <p style="font-size: 16px;">Hi ${details.repName},</p>
      <p><strong>${details.providerName}</strong> has scheduled a meeting with you:</p>

      <div style="background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin: 20px 0;">
        <p style="margin: 8px 0;"><strong>Date:</strong> ${details.meetingDate}</p>
        <p style="margin: 8px 0;"><strong>Time:</strong> ${details.meetingTime}</p>
        <p style="margin: 8px 0;"><strong>Location:</strong> ${details.locationName}</p>
        ${details.restaurantName ? `<p style="margin: 8px 0;"><strong>Restaurant:</strong> ${details.restaurantName}</p>` : ''}
      </div>

      <div style="background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 16px; margin: 20px 0;">
        <p style="margin: 0; font-weight: 600; color: #92400e;">Want to manage all your meetings in one place?</p>
        <p style="margin: 8px 0 0; color: #92400e;">Join RepConnect - it's free for sales representatives!</p>
      </div>

      <div style="text-align: center; margin: 30px 0;">
        <a href="https://login.meetwithreps.com" style="display: inline-block; background: #2563eb; color: white; padding: 14px 28px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px;">
          Join RepConnect Free
        </a>
      </div>

      <p style="font-size: 14px; color: #6b7280;">
        RepConnect helps pharmaceutical sales reps manage their healthcare provider meetings, track schedules, and stay organized.
      </p>
    </div>
    <div style="text-align: center; padding: 20px; color: #9ca3af; font-size: 12px;">
      <p>RepConnect - Connecting Healthcare Providers with Sales Representatives</p>
    </div>
  </div>
</body>
</html>`;
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Location</h3>
              <LocationSelector
                customerId={currentUser?.id}
                selectedLocation={meetingData.location}
                onSelectLocation={(location) => {
                  setMeetingData(prev => ({ ...prev, location, date: '', time: '' }));
                }}
                onAddNewLocation={() => setCurrentView('locations')}
              />
            </div>

            {meetingData.location && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Date & Time</h3>
                <SmartCalendar
                  customerId={currentUser?.id}
                  locationId={meetingData.location?.location_id || meetingData.location?.id}
                  selectedDate={meetingData.date}
                  selectedTime={meetingData.time}
                  onDateTimeSelect={(date, time) => {
                    if (time) {
                      // Time selected
                      setMeetingData(prev => ({ ...prev, time }));
                    } else {
                      // Date selected (time is null when changing date)
                      setMeetingData(prev => ({ ...prev, date, time: '' }));
                    }
                  }}
                  salesRepId={null}  // Provider viewing their own availability
                />
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Sales Representative</h3>
            <p className="text-sm text-gray-600 mb-4">
              Search for a sales rep on RepConnect or enter their information manually. They will receive an email notification about this meeting.
            </p>
            <SalesRepSelector
              selectedRep={meetingData.salesRep}
              onRepSelect={(rep) => setMeetingData(prev => ({ ...prev, salesRep: rep }))}
              onRepClear={() => setMeetingData(prev => ({ ...prev, salesRep: null }))}
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Restaurant (Optional)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add a restaurant if you've already planned where to have lunch.
            </p>

            {meetingData.restaurant ? (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <Utensils className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{meetingData.restaurant.name}</p>
                      {meetingData.restaurant.address && (
                        <p className="text-sm text-gray-600">{meetingData.restaurant.address}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setMeetingData(prev => ({ ...prev, restaurant: null }))}
                    className="text-gray-400 hover:text-gray-600 p-2"
                  >
                    <span className="text-sm">Change</span>
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowRestaurantSearch(true)}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-orange-400 hover:text-orange-600 transition-colors"
              >
                <Utensils className="w-6 h-6 mx-auto mb-2" />
                <span className="block text-sm font-medium">Search for a restaurant</span>
              </button>
            )}

            <p className="text-xs text-gray-500 text-center">
              You can skip this step and add restaurant details later
            </p>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Meeting Details</h3>

            {/* Summary Card */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-4">
              {/* Date & Time */}
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{formatDateForDisplay(meetingData.date)}</p>
                  <p className="text-sm text-gray-600">{meetingData.time}</p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Building className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {meetingData.location?.practice_locations?.location_name || meetingData.location?.location_name || 'Location'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {meetingData.location?.practice_locations?.address || meetingData.location?.address}
                  </p>
                </div>
              </div>

              {/* Sales Rep */}
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{meetingData.salesRep?.name}</p>
                  {meetingData.salesRep?.company && (
                    <p className="text-sm text-gray-600">{meetingData.salesRep.company}</p>
                  )}
                  <p className="text-sm text-gray-500">{meetingData.salesRep?.email}</p>
                  {meetingData.salesRep?.phone && (
                    <p className="text-sm text-gray-500">{meetingData.salesRep.phone}</p>
                  )}
                  {meetingData.salesRep?.isExternal && (
                    <span className="inline-flex items-center px-2 py-0.5 mt-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                      External Rep
                    </span>
                  )}
                </div>
              </div>

              {/* Restaurant */}
              {meetingData.restaurant && (
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Utensils className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{meetingData.restaurant.name}</p>
                    {meetingData.restaurant.address && (
                      <p className="text-sm text-gray-600">{meetingData.restaurant.address}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={meetingData.notes}
                onChange={(e) => setMeetingData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any special instructions or topics to discuss..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Info message */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This meeting will be automatically confirmed and added to your calendar. The sales rep will receive an email notification.
              </p>
            </div>

            {error && (
              <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Success screen
  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Meeting Added!</h2>
          <p className="text-gray-600 mb-6">
            Your meeting has been added to your calendar and {meetingData.salesRep?.name} has been notified.
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-gray-600 mb-1">{formatDateForDisplay(meetingData.date)}</p>
            <p className="font-medium text-gray-900">{meetingData.time}</p>
            <p className="text-sm text-gray-600 mt-2">with {meetingData.salesRep?.name}</p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => setCurrentView('meetings')}
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
            >
              View Calendar
            </button>
            <button
              onClick={() => {
                setSubmitSuccess(false);
                setStep(1);
                setMeetingData({
                  location: null,
                  date: '',
                  time: '',
                  salesRep: null,
                  restaurant: null,
                  notes: ''
                });
              }}
              className="w-full py-3 text-blue-600 font-medium hover:bg-blue-50 rounded-xl transition-colors"
            >
              Add Another Meeting
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button
            onClick={() => setCurrentView('meetings')}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Add Meeting</h1>
          <div className="w-9" /> {/* Spacer */}
        </div>
      </div>

      {/* Step Indicator */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            {steps.map((s, index) => (
              <React.Fragment key={s.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      step > s.number
                        ? 'bg-green-600 text-white'
                        : step === s.number
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step > s.number ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <s.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${step >= s.number ? 'text-gray-900' : 'text-gray-500'}`}>
                    {s.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded ${
                      step > s.number ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          {renderStepContent()}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button
            onClick={() => step > 1 && setStep(step - 1)}
            disabled={step === 1}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              step === 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          {step < 4 ? (
            <button
              onClick={() => canProceed() && setStep(step + 1)}
              disabled={!canProceed()}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                canProceed()
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span>Continue</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Add Meeting</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Restaurant Search Modal */}
      {showRestaurantSearch && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowRestaurantSearch(false)}
          />
          <div className="relative bg-white w-full sm:max-w-lg sm:rounded-xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Search Restaurants</h3>
              <button
                onClick={() => setShowRestaurantSearch(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              <YelpRestaurantSearch
                onRestaurantSelect={(restaurant) => {
                  setMeetingData(prev => ({ ...prev, restaurant }));
                  setShowRestaurantSearch(false);
                }}
                selectedLocation={meetingData.location?.practice_locations?.address || meetingData.location?.address}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bottom padding for fixed footer */}
      <div className="h-20" />
    </div>
  );
};

export default AddMeetingManuallyView;
